'use client'

import { Download, Pencil, Plus, Trash2, Upload } from 'lucide-react'
import * as React from 'react'

import { cn } from '../cn'
import { AddressInput, type AddressSuggestion } from './address-input'
import { Button } from './button'
import { Checkbox } from './checkbox'
import { DatePicker } from './date-picker'
import { Input } from './input'
import { Label } from './label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select'
import { Textarea } from './textarea'

export type OnboardingFormFieldType =
  | 'text'
  | 'textarea'
  | 'email'
  | 'phone'
  | 'date'
  | 'number'
  | 'checkbox'
  | 'single_select'
  | 'multi_select'
  | 'document_upload'
  | 'address'
  | 'rows'
  | 'order_group'

/** Human labels for every field type — shared by the builder and the picker. */
export const ONBOARDING_FIELD_TYPE_LABELS: Record<OnboardingFormFieldType, string> = {
  text: 'Short text',
  textarea: 'Long text',
  email: 'Email',
  phone: 'Phone',
  date: 'Date',
  number: 'Number',
  checkbox: 'Checkbox',
  single_select: 'Single select',
  multi_select: 'Multi select',
  document_upload: 'Document upload',
  address: 'Address',
  rows: 'Table',
  // Built-in only — used by the default meal-service task, not authored here.
  order_group: 'Order groups',
}

export interface OnboardingFormFieldOption {
  label: string
  value: string
}

export interface OnboardingFormField {
  id: string
  label: string
  type: OnboardingFormFieldType
  required?: boolean
  placeholder?: string
  helpText?: string
  options?: OnboardingFormFieldOption[]
  /** For `rows`: the columns each row collects (text cells). */
  columns?: OnboardingFormRowColumn[]
  /** For `order_group`: sibling field + value that enables per-group grade selection. */
  gradeWhen?: { field: string; equals: string }
  /**
   * A lookup-backed select whose per-school option source isn't configured
   * yet — rendered as a note, never blocking submission.
   */
  lookupPending?: boolean
}

/** One row of an `order_group` field value. */
export interface OnboardingOrderGroup {
  name: string
  grades: string[]
}

/** One column definition of a `rows` (table) field. */
export interface OnboardingFormRowColumn {
  id: string
  label: string
}

/** Structured value of an `address` field. Part names mirror the Address model. */
export interface OnboardingAddressValue {
  line1: string
  line2?: string
  city: string
  stateProvince: string
  postalCode: string
  /** Full display string from the autocomplete provider, when one was used. */
  formatted?: string
}

/** Parse a stored `address` value into a clean shape (tolerant of garbage). */
export function readAddressValue(value: unknown): OnboardingAddressValue {
  const record = value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {}
  const part = (key: string) => (typeof record[key] === 'string' ? (record[key] as string) : '')
  return {
    line1: part('line1'),
    ...(part('line2') ? { line2: part('line2') } : {}),
    city: part('city'),
    stateProvince: part('stateProvince'),
    postalCode: part('postalCode'),
    ...(part('formatted') ? { formatted: part('formatted') } : {}),
  }
}

/** Parse a stored `rows` value into clean row records (tolerant of garbage). */
export function readRowsValue(value: unknown, columns: readonly OnboardingFormRowColumn[]): Record<string, string>[] {
  if (!Array.isArray(value)) {
    return []
  }
  return value
    .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object' && !Array.isArray(item))
    .map((item) => {
      const row: Record<string, string> = {}
      for (const column of columns) {
        row[column.id] = typeof item[column.id] === 'string' ? (item[column.id] as string) : ''
      }
      return row
    })
}

/** A document the user attached, encoded for upload. Keyed back to its field via `fieldId`. */
export interface OnboardingFormDocumentUpload {
  fieldId: string
  title: string
  fileName: string
  contentType: string | null
  contentBase64: string
}

export interface OnboardingTaskFormSubmit {
  response: Record<string, unknown>
  documents: OnboardingFormDocumentUpload[]
}

export interface OnboardingTaskFormProps {
  fields: OnboardingFormField[]
  /** Existing saved values (e.g. a previously submitted response). */
  initialResponse?: Record<string, unknown>
  /** Render the submitted response as a read-only data display (label → value)
   *  instead of an editable form. */
  readOnly?: boolean
  /** When `readOnly`, show an "Edit" action that flips the data display back
   *  into the editable form (with Save / Cancel). Opt-in because not every
   *  read-only context allows re-submitting (e.g. canceled tasks). */
  allowEdit?: boolean
  /** Submit is in flight. */
  submitting?: boolean
  /** Primary action label. Default `Submit`. */
  submitLabel?: string
  /** Label on the primary action when re-saving an edited read-only response.
   *  Default `Save changes`. */
  saveLabel?: string
  /** Hide the built-in action row entirely (host renders its own). */
  hideActions?: boolean
  /** Extra controls rendered left of the primary action (e.g. a Back button). */
  secondaryActions?: React.ReactNode
  /** Resolve a downloadable URL for a submitted document, so the read-only
   *  display renders it as a link instead of plain filename text. Return
   *  undefined to fall back to plain text (e.g. when the host can't serve it). */
  resolveDocumentHref?: (doc: SubmittedDocumentRef) => string | undefined
  /**
   * Real-address autocomplete backend for `address` fields (typically thin
   * wrappers over the host's /api/places proxy). Absent → manual entry only.
   */
  addressAutocomplete?: {
    fetchSuggestions: (input: string) => Promise<AddressSuggestion[]>
    fetchDetails: (placeId: string) => Promise<OnboardingAddressValue | null>
  }
  onSubmit?: (input: OnboardingTaskFormSubmit) => void | Promise<void>
  className?: string
}

/** A document as stored in a submitted task response (see SchoolOnboardingManager). */
export interface SubmittedDocumentRef {
  id?: string
  title?: string | null
  fileName?: string | null
  contentType?: string | null
}

type FormState = Record<string, unknown>
type UploadState = Record<string, OnboardingFormDocumentUpload[]>

/**
 * Schema-driven renderer for school onboarding tasks. Owns response + upload
 * state, renders each field by type, and hands the collected response and
 * encoded documents to `onSubmit`. Shared by the Ordo school-facing task page
 * and the Customers internal onboarding detail panel.
 */
export function OnboardingTaskForm({
  fields,
  initialResponse,
  readOnly = false,
  allowEdit = false,
  submitting = false,
  submitLabel = 'Submit',
  saveLabel = 'Save changes',
  hideActions = false,
  secondaryActions,
  resolveDocumentHref,
  addressAutocomplete,
  onSubmit,
  className,
}: OnboardingTaskFormProps) {
  const initial = React.useMemo(() => initialResponse ?? {}, [initialResponse])
  const [response, setResponse] = React.useState<FormState>({})
  const [uploads, setUploads] = React.useState<UploadState>({})
  // When read-only + allowEdit, "Edit" flips the data display into the editable
  // form. Submitting re-saves via the same onSubmit path the host already wires.
  const [editing, setEditing] = React.useState(false)

  const showReadout = readOnly && !editing
  const values = showReadout ? initial : { ...initial, ...response }
  const disabled = showReadout || submitting

  function cancelEdit() {
    setEditing(false)
    setResponse({})
    setUploads({})
  }

  async function handleSubmit() {
    if (disabled || !onSubmit) {
      return
    }
    await onSubmit({ response: values, documents: Object.values(uploads).flat() })
    if (editing) {
      cancelEdit()
    }
  }

  return (
    <div className={cn('space-y-5', className)}>
      <div className="space-y-5">
        {fields.map((field) =>
          showReadout ? (
            <OnboardingFieldReadout
              key={field.id}
              field={field}
              value={values[field.id]}
              resolveDocumentHref={resolveDocumentHref}
            />
          ) : (
            <OnboardingField
              key={field.id}
              field={field}
              value={values[field.id]}
              formValues={values}
              disabled={disabled}
              uploads={uploads[field.id] ?? []}
              resolveDocumentHref={resolveDocumentHref}
              addressAutocomplete={addressAutocomplete}
              onChange={(value) => setResponse((current) => ({ ...current, [field.id]: value }))}
              onFiles={(docs) => setUploads((current) => ({ ...current, [field.id]: docs }))}
            />
          ),
        )}
      </div>

      {hideActions ? null : showReadout ? (
        (secondaryActions || allowEdit) && (
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            {secondaryActions}
            {allowEdit ? (
              <Button type="button" variant="outline" onClick={() => setEditing(true)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
            ) : null}
          </div>
        )
      ) : (
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {editing ? (
            <Button type="button" variant="ghost" disabled={submitting} onClick={cancelEdit}>
              Cancel
            </Button>
          ) : (
            secondaryActions
          )}
          <Button type="button" disabled={disabled} onClick={() => void handleSubmit()}>
            {submitting ? 'Saving...' : editing ? saveLabel : submitLabel}
          </Button>
        </div>
      )}
    </div>
  )
}

function OnboardingField({
  field,
  value,
  formValues,
  disabled,
  uploads,
  resolveDocumentHref,
  addressAutocomplete,
  onChange,
  onFiles,
}: {
  field: OnboardingFormField
  value: unknown
  formValues: Record<string, unknown>
  disabled: boolean
  uploads: OnboardingFormDocumentUpload[]
  resolveDocumentHref?: (doc: SubmittedDocumentRef) => string | undefined
  addressAutocomplete?: OnboardingTaskFormProps['addressAutocomplete']
  onChange: (value: unknown) => void
  onFiles: (uploads: OnboardingFormDocumentUpload[]) => void
}) {
  // `order_group` owns its full layout (rows + add/remove), so it renders
  // outside the shared Label wrapper used by the scalar fields.
  if (field.type === 'order_group') {
    const collectGrades = field.gradeWhen ? formValues[field.gradeWhen.field] === field.gradeWhen.equals : true
    return (
      <OrderGroupField
        field={field}
        value={value}
        disabled={disabled}
        collectGrades={collectGrades}
        onChange={onChange}
      />
    )
  }
  return (
    <div className="space-y-2">
      <Label htmlFor={field.id} className="text-sm font-medium">
        {field.label}
        {field.required ? <span className="ml-1 text-destructive">*</span> : null}
      </Label>
      {field.type === 'textarea' ? (
        <Textarea
          id={field.id}
          value={typeof value === 'string' ? value : ''}
          placeholder={field.placeholder}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : field.type === 'checkbox' ? (
        <div className="flex min-h-10 items-center gap-2">
          <Checkbox
            id={field.id}
            checked={value === true}
            disabled={disabled}
            onCheckedChange={(next) => onChange(Boolean(next))}
          />
        </div>
      ) : field.lookupPending && (field.type === 'single_select' || field.type === 'multi_select') ? (
        <p className="rounded-md border border-dashed bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          The choices for this question aren't configured for your school yet — you can skip it and finish the rest of
          the task.
        </p>
      ) : field.type === 'single_select' ? (
        <Select value={typeof value === 'string' ? value : ''} disabled={disabled} onValueChange={onChange}>
          <SelectTrigger id={field.id}>
            <SelectValue placeholder={field.placeholder ?? 'Select'} />
          </SelectTrigger>
          <SelectContent>
            {(field.options ?? []).map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : field.type === 'multi_select' ? (
        <MultiSelectField field={field} value={value} disabled={disabled} onChange={onChange} />
      ) : field.type === 'address' ? (
        <AddressInput
          id={field.id}
          value={readAddressValue(value)}
          disabled={disabled}
          fetchSuggestions={addressAutocomplete?.fetchSuggestions}
          fetchDetails={addressAutocomplete?.fetchDetails}
          onChange={onChange}
        />
      ) : field.type === 'rows' ? (
        <RowsField field={field} value={value} disabled={disabled} onChange={onChange} />
      ) : field.type === 'document_upload' ? (
        <DocumentUploadField
          field={field}
          value={value}
          uploads={uploads}
          disabled={disabled}
          resolveDocumentHref={resolveDocumentHref}
          onFiles={onFiles}
        />
      ) : field.type === 'date' ? (
        <DatePicker
          value={typeof value === 'string' ? value : ''}
          disabled={disabled}
          placeholder={field.placeholder ?? 'Pick a date'}
          onChange={onChange}
          // Match the sibling text Input's field shape. The DatePicker trigger is
          // a Button, which inherits the app-wide pill radius (`--btn-radius` on
          // [data-slot=button]); `!rounded-md` overrides that unlayered rule so the
          // date field reads as a form field, not a pill.
          className="h-10 w-full !rounded-md"
        />
      ) : (
        <Input
          id={field.id}
          type={field.type === 'email' ? 'email' : field.type === 'number' ? 'number' : 'text'}
          value={typeof value === 'string' || typeof value === 'number' ? String(value) : ''}
          placeholder={field.placeholder}
          disabled={disabled}
          onChange={(event) => onChange(field.type === 'number' ? event.target.valueAsNumber : event.target.value)}
        />
      )}
      {field.helpText ? <p className="text-xs text-muted-foreground">{field.helpText}</p> : null}
    </div>
  )
}

/** Read-only label → value display for a submitted field. */
function OnboardingFieldReadout({
  field,
  value,
  resolveDocumentHref,
}: {
  field: OnboardingFormField
  value: unknown
  resolveDocumentHref?: (doc: SubmittedDocumentRef) => string | undefined
}) {
  if (field.type === 'document_upload') {
    const docs = Array.isArray(value)
      ? value.filter((d): d is SubmittedDocumentRef => !!d && typeof d === 'object')
      : []
    return (
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">{field.label}</p>
        {docs.length > 0 ? (
          <ul className="space-y-1 text-sm">
            {docs.map((doc, index) => {
              const name = String(doc.fileName ?? doc.title ?? 'Document')
              const href = resolveDocumentHref?.(doc)
              return (
                <li key={doc.id ?? `${name}-${index}`}>
                  {href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 font-medium text-primary underline-offset-2 hover:underline">
                      <Download className="h-3.5 w-3.5" />
                      {name}
                    </a>
                  ) : (
                    name
                  )}
                </li>
              )
            })}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">—</p>
        )}
      </div>
    )
  }

  if (field.type === 'order_group') {
    const groups = readOrderGroups(value)
    return (
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">{field.label}</p>
        {groups.length > 0 ? (
          <ul className="space-y-1 text-sm">
            {groups.map((group, index) => (
              <li key={`${group.name}-${index}`}>
                <span className="font-medium">{group.name}</span>
                {group.grades.length > 0 ? (
                  <span className="text-muted-foreground"> · {gradeLabels(group.grades).join(', ')}</span>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">—</p>
        )}
      </div>
    )
  }

  if (field.type === 'rows') {
    const columns = field.columns ?? []
    const rows = readRowsValue(value, columns).filter((row) => Object.values(row).some((cell) => cell.trim()))
    return (
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">{field.label}</p>
        {rows.length > 0 ? (
          <ul className="space-y-1 text-sm">
            {rows.map((row, index) => (
              <li key={index}>
                {columns
                  .map((column) => row[column.id])
                  .filter((cell) => cell && cell.trim())
                  .join(' · ')}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">—</p>
        )}
      </div>
    )
  }

  const readout = formatReadout(field, value)
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground">{field.label}</p>
      <p className={cn('text-sm', !readout && 'text-muted-foreground')}>{readout || '—'}</p>
    </div>
  )
}

/** Formats a stored scalar field value for the read-only display. */
function formatReadout(field: OnboardingFormField, value: unknown): string {
  switch (field.type) {
    case 'checkbox':
      return value === true ? 'Yes' : 'No'
    case 'address': {
      if (typeof value === 'string') {
        return value
      }
      const address = readAddressValue(value)
      if (address.formatted) {
        return address.formatted
      }
      return [address.line1, address.line2, address.city, address.stateProvince, address.postalCode]
        .filter((part): part is string => Boolean(part && part.trim()))
        .join(', ')
    }
    case 'date':
      return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)
        ? new Date(`${value.slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        : ''
    case 'single_select': {
      const option = (field.options ?? []).find((o) => o.value === value)
      return option?.label ?? (typeof value === 'string' ? value : '')
    }
    case 'multi_select': {
      const selected = Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []
      return selected.map((v) => (field.options ?? []).find((o) => o.value === v)?.label ?? v).join(', ')
    }
    default:
      return typeof value === 'string' || typeof value === 'number' ? String(value) : ''
  }
}

/**
 * Grade/age catalog for the per-group grade picker, grouped by band. Codes
 * mirror the shared GradeRange catalog (`schoolGradeGroups` in the customers
 * app + `schoolGradeRangeSeeds` in @ordo/schema). Kept local so this primitive
 * has no app dependency.
 */
const ORDER_GROUP_GRADE_BANDS: ReadonlyArray<{ label: string; options: ReadonlyArray<[code: string, label: string]> }> =
  [
    {
      label: 'Preschool',
      options: [
        ['infant', 'Infant'],
        ['age-1-2', 'Age 1-2'],
        ['age-3-5', 'Age 3-5'],
      ],
    },
    {
      label: 'Elementary',
      options: [
        ['k', 'K'],
        ['1', '1'],
        ['2', '2'],
        ['3', '3'],
        ['4', '4'],
        ['5', '5'],
      ],
    },
    {
      label: 'Middle',
      options: [
        ['6', '6'],
        ['7', '7'],
        ['8', '8'],
      ],
    },
    {
      label: 'High',
      options: [
        ['9', '9'],
        ['10', '10'],
        ['11', '11'],
        ['12', '12'],
      ],
    },
    { label: 'Adult', options: [['adult', 'Adult']] },
  ]

const GRADE_CODE_LABEL = new Map(
  ORDER_GROUP_GRADE_BANDS.flatMap((band) => band.options.map(([code, label]) => [code, label])),
)

function gradeLabels(codes: string[]): string[] {
  return codes.map((code) => GRADE_CODE_LABEL.get(code) ?? code)
}

/** Parse a stored `order_group` value into clean rows (tolerant of garbage). */
function readOrderGroups(value: unknown): OnboardingOrderGroup[] {
  if (!Array.isArray(value)) {
    return []
  }
  const rows: OnboardingOrderGroup[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') {
      continue
    }
    const record = item as Record<string, unknown>
    const name = typeof record['name'] === 'string' ? record['name'] : ''
    const grades = Array.isArray(record['grades'])
      ? record['grades'].filter((g): g is string => typeof g === 'string')
      : []
    rows.push({ name, grades })
  }
  return rows
}

/**
 * Repeatable "order group" builder. Each row is a name plus (in grade mode)
 * the grades it covers, picked from the band-grouped catalog. In homeroom mode
 * (`collectGrades` false) rows are name-only. Value: Array<{name, grades[]}>.
 */
function OrderGroupField({
  field,
  value,
  disabled,
  collectGrades,
  onChange,
}: {
  field: OnboardingFormField
  value: unknown
  disabled: boolean
  collectGrades: boolean
  onChange: (value: OnboardingOrderGroup[]) => void
}) {
  const groups = readOrderGroups(value)

  function update(next: OnboardingOrderGroup[]) {
    onChange(next)
  }
  function setName(index: number, name: string) {
    update(groups.map((g, i) => (i === index ? { ...g, name } : g)))
  }
  function toggleGrade(index: number, code: string, checked: boolean) {
    update(
      groups.map((g, i) =>
        i === index ? { ...g, grades: checked ? [...g.grades, code] : g.grades.filter((c) => c !== code) } : g,
      ),
    )
  }
  function addGroup() {
    update([...groups, { name: '', grades: [] }])
  }
  function removeGroup(index: number) {
    update(groups.filter((_, i) => i !== index))
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <span className="text-sm font-medium">
          {field.label}
          {field.required ? <span className="ml-1 text-destructive">*</span> : null}
        </span>
        {field.helpText ? <p className="text-xs text-muted-foreground">{field.helpText}</p> : null}
      </div>

      {groups.map((group, index) => (
        <div key={index} className="space-y-3 rounded-md border p-3">
          <div className="flex items-center gap-2">
            <Input
              value={group.name}
              placeholder={collectGrades ? 'Group name (e.g. Lower School)' : 'Homeroom name (e.g. Room 101)'}
              disabled={disabled}
              onChange={(event) => setName(index, event.target.value)}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 text-destructive hover:bg-destructive/10"
              disabled={disabled}
              aria-label="Remove group"
              onClick={() => removeGroup(index)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          {collectGrades ? (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Grades in this group</p>
              <div className="grid gap-3 sm:grid-cols-2">
                {ORDER_GROUP_GRADE_BANDS.map((band) => (
                  <div key={band.label} className="rounded-md border p-2">
                    <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {band.label}
                    </p>
                    <div className="grid grid-cols-3 gap-1.5">
                      {band.options.map(([code, label]) => (
                        <label key={code} className="flex items-center gap-1.5 text-sm">
                          <Checkbox
                            checked={group.grades.includes(code)}
                            disabled={disabled}
                            onCheckedChange={(next) => toggleGrade(index, code, next === true)}
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ))}

      <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={addGroup}>
        <Plus className="h-4 w-4" />
        {collectGrades ? 'Add order group' : 'Add homeroom'}
      </Button>
    </div>
  )
}

/**
 * Repeating-rows editor for a `rows` (table) field: one text input per
 * author-defined column, add/remove rows. Value: Array<Record<columnId, string>>.
 */
function RowsField({
  field,
  value,
  disabled,
  onChange,
}: {
  field: OnboardingFormField
  value: unknown
  disabled: boolean
  onChange: (value: Record<string, string>[]) => void
}) {
  const columns = field.columns ?? []
  const rows = readRowsValue(value, columns)

  function emptyRow(): Record<string, string> {
    return Object.fromEntries(columns.map((column) => [column.id, '']))
  }
  function setCell(rowIndex: number, columnId: string, cell: string) {
    onChange(rows.map((row, i) => (i === rowIndex ? { ...row, [columnId]: cell } : row)))
  }

  if (columns.length === 0) {
    return (
      <p className="rounded-md border border-dashed bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
        This table has no columns configured yet — you can skip it and finish the rest of the task.
      </p>
    )
  }

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40 text-left">
              {columns.map((column) => (
                <th key={column.id} className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                  {column.label}
                </th>
              ))}
              <th className="w-10 px-2 py-1.5" aria-label="Row actions" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="px-2 py-3 text-center text-sm text-muted-foreground">
                  No rows yet
                </td>
              </tr>
            ) : (
              rows.map((row, rowIndex) => (
                <tr key={rowIndex} className="border-b last:border-b-0">
                  {columns.map((column) => (
                    <td key={column.id} className="px-1 py-1">
                      <Input
                        value={row[column.id] ?? ''}
                        aria-label={`${column.label}, row ${rowIndex + 1}`}
                        disabled={disabled}
                        className="h-8"
                        onChange={(event) => setCell(rowIndex, column.id, event.target.value)}
                      />
                    </td>
                  ))}
                  <td className="px-1 py-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:bg-destructive/10"
                      disabled={disabled}
                      aria-label={`Remove row ${rowIndex + 1}`}
                      onClick={() => onChange(rows.filter((_, i) => i !== rowIndex))}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => onChange([...rows, emptyRow()])}>
        <Plus className="h-4 w-4" />
        Add row
      </Button>
    </div>
  )
}

function MultiSelectField({
  field,
  value,
  disabled,
  onChange,
}: {
  field: OnboardingFormField
  value: unknown
  disabled: boolean
  onChange: (value: string[]) => void
}) {
  const selected = Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []
  return (
    <div className="grid gap-2 rounded-md border p-3 sm:grid-cols-2">
      {(field.options ?? []).map((option) => {
        const checked = selected.includes(option.value)
        return (
          <label key={option.value} className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={checked}
              disabled={disabled}
              onCheckedChange={(next) => {
                onChange(next ? [...selected, option.value] : selected.filter((v) => v !== option.value))
              }}
            />
            {option.label}
          </label>
        )
      })}
    </div>
  )
}

function DocumentUploadField({
  field,
  value,
  uploads,
  disabled,
  resolveDocumentHref,
  onFiles,
}: {
  field: OnboardingFormField
  value: unknown
  uploads: OnboardingFormDocumentUpload[]
  disabled: boolean
  resolveDocumentHref?: (doc: SubmittedDocumentRef) => string | undefined
  onFiles: (uploads: OnboardingFormDocumentUpload[]) => void
}) {
  // Previously submitted documents riding along in the response. The server
  // keeps them unless this edit stages new uploads for the same field — make
  // that contract visible instead of showing an empty upload box.
  const existingDocs = Array.isArray(value)
    ? value.filter((d): d is SubmittedDocumentRef => !!d && typeof d === 'object' && !Array.isArray(d))
    : []

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) {
      onFiles([])
      return
    }
    const docs = await Promise.all(
      Array.from(files).map(async (file) => ({
        fieldId: field.id,
        title: file.name,
        fileName: file.name,
        contentType: file.type || null,
        contentBase64: await fileToBase64(file),
      })),
    )
    onFiles(docs)
  }

  return (
    <div className="space-y-2">
      {existingDocs.length > 0 ? (
        <div className="space-y-1 rounded-md border bg-muted/30 p-3">
          <p className="text-xs font-medium text-muted-foreground">Submitted files</p>
          <ul className="space-y-1 text-sm">
            {existingDocs.map((doc, index) => {
              const name = String(doc.fileName ?? doc.title ?? 'Document')
              const href = resolveDocumentHref?.(doc)
              return (
                <li key={doc.id ?? `${name}-${index}`}>
                  {href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 font-medium text-primary underline-offset-2 hover:underline">
                      <Download className="h-3.5 w-3.5" />
                      {name}
                    </a>
                  ) : (
                    name
                  )}
                </li>
              )
            })}
          </ul>
          <p className="text-xs text-muted-foreground">
            {uploads.length > 0
              ? 'Uploading new files will replace the existing ones.'
              : 'Existing files are kept unless you upload replacements.'}
          </p>
        </div>
      ) : null}
      <label className="flex min-h-24 cursor-pointer flex-col items-center justify-center gap-2 rounded-md border border-dashed px-4 py-5 text-center text-sm text-muted-foreground transition-colors hover:bg-muted/50">
        <Upload className="h-5 w-5" />
        <span>
          {uploads.length > 0
            ? `${uploads.length} file${uploads.length === 1 ? '' : 's'} selected`
            : 'Upload documents'}
        </span>
        <input
          className="sr-only"
          type="file"
          multiple
          disabled={disabled}
          onChange={(event) => void handleFiles(event.target.files)}
        />
      </label>
      {uploads.length > 0 ? (
        <ul className="space-y-1 text-xs text-muted-foreground">
          {uploads.map((upload) => (
            <li key={`${upload.fileName}-${upload.contentBase64.length}`}>{upload.fileName}</li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

/**
 * Read a File into a bare base64 string (no `data:` prefix) — the wire shape
 * every Ordo inline-bytes upload uses. Exported because the special-diet note
 * upload needs the identical encoding; two hand-rolled readers is how one of
 * them ends up shipping the data-URL prefix into `Document.content`.
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : ''
      resolve(result.includes(',') ? result.slice(result.indexOf(',') + 1) : result)
    }
    reader.onerror = () => reject(reader.error ?? new Error('Unable to read file'))
    reader.readAsDataURL(file)
  })
}
